/* eslint-disable no-nested-ternary */
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Box, Card, Stack, Divider, Typography } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import FormProvider, { RHFSwitch } from 'src/components/hook-form';
import { SettingType, SystemConfigKey } from 'src/type/settings';
import { toastError } from 'src/utils/format-error';
import * as Yup from 'yup';
import { enqueueSnackbar } from 'notistack';
import { useBoolean } from 'src/hooks/use-boolean';

import { useState, useEffect } from 'react';
import LoadingContainer from 'src/components/ui-kit/loading-container';
import axios, { endpoints } from 'src/utils/axios';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import UpdatedText from 'src/components/ui-kit/updated-text';
import { getSettingValue } from '../../utils';
import { findLatestUpdated } from './utils';

const Schema = Yup.object().shape({
  [SystemConfigKey.SUNDAY]: Yup.boolean(),
  [SystemConfigKey.MONDAY]: Yup.boolean(),
  [SystemConfigKey.TUESDAY]: Yup.boolean(),
  [SystemConfigKey.WEDNESDAY]: Yup.boolean(),
  [SystemConfigKey.THURSDAY]: Yup.boolean(),
  [SystemConfigKey.FRIDAY]: Yup.boolean(),
  [SystemConfigKey.SATURDAY]: Yup.boolean(),
  [SystemConfigKey.WEEKLY_START_TIME]: Yup.mixed<Dayjs>().required(),
  [SystemConfigKey.WEEKLY_END_TIME]: Yup.mixed<Dayjs>().required(),
});

const inputs = [
  {
    title: 'วันอาทิตย์',
    name: SystemConfigKey.SUNDAY,
  },
  {
    title: 'วันจันทร์',
    name: SystemConfigKey.MONDAY,
  },
  {
    title: 'วันอังคาร',
    name: SystemConfigKey.TUESDAY,
  },
  {
    title: 'วันพุธ',
    name: SystemConfigKey.WEDNESDAY,
  },
  {
    title: 'วันพฤหัสบดี',
    name: SystemConfigKey.THURSDAY,
  },
  {
    title: 'วันศุกร์',
    name: SystemConfigKey.FRIDAY,
  },
  {
    title: 'วันเสาร์',
    name: SystemConfigKey.SATURDAY,
  },
];

export default function SettingWeekly() {
  const reload = useBoolean();
  const initialize = useBoolean(true);
  const startError = useBoolean(false);
  const endError = useBoolean(false);
  const timeError = useBoolean(false);

  const [setting, setSetting] = useState<SettingType[]>([]);

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(Schema),
    defaultValues: {
      [SystemConfigKey.SUNDAY]: false,
      [SystemConfigKey.MONDAY]: false,
      [SystemConfigKey.TUESDAY]: false,
      [SystemConfigKey.WEDNESDAY]: false,
      [SystemConfigKey.THURSDAY]: false,
      [SystemConfigKey.FRIDAY]: false,
      [SystemConfigKey.SATURDAY]: false,
      [SystemConfigKey.WEEKLY_START_TIME]: null as unknown as Dayjs,
      [SystemConfigKey.WEEKLY_END_TIME]: null as unknown as Dayjs,
    },
  });

  const {
    handleSubmit,
    reset,
    watch,
    control,
    getValues,
    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    load();
  }, [reload.value]);

  const load = async () => {
    try {
      const { data } = await axios.get<SettingType[]>(endpoints.settings.get);

      setSetting(data);

      const startTimeStr = getSettingValue(data, SystemConfigKey.WEEKLY_START_TIME) as string;
      const endTimeStr = getSettingValue(data, SystemConfigKey.WEEKLY_END_TIME) as string;

      const startTime = startTimeStr ? dayjs(startTimeStr, 'HH:mm') : (null as unknown as Dayjs);
      const endTime = endTimeStr ? dayjs(endTimeStr, 'HH:mm') : (null as unknown as Dayjs);

      reset({
        [SystemConfigKey.SUNDAY]: getSettingValue(data, SystemConfigKey.SUNDAY) || false,
        [SystemConfigKey.MONDAY]: getSettingValue(data, SystemConfigKey.MONDAY) || false,
        [SystemConfigKey.TUESDAY]: getSettingValue(data, SystemConfigKey.TUESDAY) || false,
        [SystemConfigKey.WEDNESDAY]: getSettingValue(data, SystemConfigKey.WEDNESDAY) || false,
        [SystemConfigKey.THURSDAY]: getSettingValue(data, SystemConfigKey.THURSDAY) || false,
        [SystemConfigKey.FRIDAY]: getSettingValue(data, SystemConfigKey.FRIDAY) || false,
        [SystemConfigKey.SATURDAY]: getSettingValue(data, SystemConfigKey.SATURDAY) || false,
        [SystemConfigKey.WEEKLY_START_TIME]: startTime,
        [SystemConfigKey.WEEKLY_END_TIME]: endTime,
      });
    } catch (error) {
      toastError(error);
    } finally {
      initialize.onFalse();
    }
  };

  const onSubmit = handleSubmit(async (values) => {
    if (!values.WEEKLY_START_TIME) {
      startError.onTrue();
      return;
    }
    if (!values.WEEKLY_END_TIME) {
      endError.onTrue();
      return;
    }
    try {
      const body = [
        {
          key: SystemConfigKey.SUNDAY,
          value: values.SUNDAY,
        },
        {
          key: SystemConfigKey.MONDAY,
          value: values.MONDAY,
        },
        {
          key: SystemConfigKey.TUESDAY,
          value: values.TUESDAY,
        },
        {
          key: SystemConfigKey.WEDNESDAY,
          value: values.WEDNESDAY,
        },
        {
          key: SystemConfigKey.THURSDAY,
          value: values.THURSDAY,
        },
        {
          key: SystemConfigKey.FRIDAY,
          value: values.FRIDAY,
        },
        {
          key: SystemConfigKey.SATURDAY,
          value: values.SATURDAY,
        },
        {
          key: SystemConfigKey.WEEKLY_START_TIME,
          value: values.WEEKLY_START_TIME?.format('HH:mm'),
        },
        {
          key: SystemConfigKey.WEEKLY_END_TIME,
          value: values.WEEKLY_END_TIME?.format('HH:mm'),
        },
      ];

      // const body = Object.entries(values).map(([key, value]) => ({
      //   key,
      //   value,
      // }));

      await axios.post(endpoints.settings.createOrUpdate, { configs: body });
      enqueueSnackbar('บันทึกสำเร็จ');
      reload.onToggle();
    } catch (error) {
      toastError(error);
    }
  });

  const values = watch() as unknown as Record<SystemConfigKey, boolean | null | dayjs.Dayjs>;

  useEffect(() => {
    if (watch().WEEKLY_START_TIME) startError.onFalse();
    if (watch().WEEKLY_END_TIME) endError.onFalse();

    if (watch().WEEKLY_START_TIME && watch().WEEKLY_END_TIME) {
      const start = dayjs(watch().WEEKLY_START_TIME);
      const end = dayjs(watch().WEEKLY_END_TIME);

      if (start.isAfter(end)) {
        timeError.onTrue();
      } else {
        timeError.onFalse();
      }
    }
  }, [watch().WEEKLY_START_TIME, watch().WEEKLY_END_TIME]);

  return (
    <LoadingContainer loading={initialize.value} sx={{ minHeight: '30vh' }} fitContent>
      <Card sx={{ py: 5, px: 3 }}>
        <Stack spacing={3}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="h6">เวลาเปิด-ปิดระบบ</Typography>
            <Typography variant="caption" color="text.secondary">
              <UpdatedText {...findLatestUpdated(setting)} />
            </Typography>
          </Stack>

          <FormProvider methods={methods} onSubmit={onSubmit}>
            <Stack spacing={2}>
              <Stack
                spacing={2}
                direction="row"
                // justifyContent={{ xl: 'space-between' }}
                mb={2}
                flexWrap="wrap"
              >
                {inputs.map((input) => (
                  <RHFSwitch
                    key={input.name}
                    name={input.name}
                    label={
                      <Stack>
                        <Typography variant="body2">{input.title}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {values[input.name] ? 'เปิด' : 'ปิด'}
                        </Typography>
                      </Stack>
                    }
                    sx={{
                      alignItems: 'center',
                      minWidth: '125px',
                    }}
                  />
                ))}
              </Stack>
            </Stack>

            <Divider style={{ borderStyle: 'dashed' }} />

            <Stack
              spacing={2}
              my={2}
              direction="row"
              alignItems="center"
              flexWrap={{ xs: 'wrap', sm: 'nowrap' }}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
                <Controller
                  name="WEEKLY_START_TIME"
                  control={control}
                  render={({ field }) => (
                    <TimePicker
                      {...field}
                      label="เลือกเวลาเปิด"
                      value={getValues('WEEKLY_START_TIME') ? dayjs(field.value) : null}
                      onChange={(newValue) => {
                        field.onChange(newValue);
                      }}
                      sx={{ maxWidth: '250px', width: 1 }}
                      slotProps={{
                        textField: {
                          helperText: startError.value ? 'กรุณาเลือกเวลาเปิด' : null,
                          error: startError.value,
                        },
                      }}
                    />
                  )}
                />
                <Controller
                  name="WEEKLY_END_TIME"
                  control={control}
                  render={({ field }) => (
                    <TimePicker
                      {...field}
                      label="เลือกเวลาปิด"
                      value={getValues('WEEKLY_END_TIME') ? dayjs(field.value) : null}
                      onChange={(newValue) => {
                        field.onChange(newValue);
                      }}
                      sx={{ maxWidth: '250px', width: 1 }}
                      slotProps={{
                        textField: {
                          helperText: endError.value
                            ? 'กรุณาเลือกเวลาปิด'
                            : timeError.value
                              ? 'เวลาปิดต้องมากกว่าเวลาเริ่ม'
                              : null,
                          error: endError.value || timeError.value,
                        },
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Stack>

            <Box sx={{ mt: 2 }}>
              <LoadingButton
                variant="contained"
                type="submit"
                loading={isSubmitting}
                sx={{ minWidth: 120 }}
              >
                บันทึก
              </LoadingButton>
            </Box>
          </FormProvider>
        </Stack>
      </Card>
    </LoadingContainer>
  );
}
